import {
  CaseAssignmentResponse,
  ICaseAssignmentBodyReq,
  UpdateCaseAssignmentRequest,
  CaseStatusValue,
  IDataAPICaseStatus,
  UpdateCaseStatusRequest,
  ICaseReasonCode
} from "@/types/casesFrontendTypes";
import { IDataAPIUser } from "@/types/users";
import { CaseAssigneeData, getAssignmentDifference } from "@/utils/assignment";
import { createServerEndpoint, hasKey } from "@/utils/functions";
import axios from "axios";
const state = () => ({
  reasonCodes: null as ICaseReasonCode[] | null,
  currentAssignees: null as IDataAPIUser[] | null,
  assigneeData: {
    choice: null,
    list: null,
    options: null
  } as CaseAssigneeData,
  changesExist: false,
  showChangeDialog: false,
  original: null as IDataAPICaseStatus | null,
  currentValue: null as CaseStatusValue | null
});

const getters = {
  assignees(state: any): CaseAssignmentResponse[] | null {
    return state.assigneeData.list;
  },
  preselected(state: any): CaseAssignmentResponse[] | null {
    return state.assigneeData.choice;
  },
  assigneeOptions(state: any): IDataAPIUser[] | null {
    return state.assigneeData.options;
  },
  assigneeDifference(state: any): CaseAssignmentResponse[] | null {
    return getAssignmentDifference(
      state.assigneeData.list,
      state.assigneeData.options
    );
  },
  original(state: any): IDataAPICaseStatus | null {
    return state.original;
  }
};

const mutations = {
  UPDATE_ASSIGNEE_DATA(state: any, payload: Partial<CaseAssigneeData>) {
    Object.keys(payload).forEach((key) => {
      if (hasKey(state.assigneeData, key)) {
        state.assigneeData[key as keyof CaseAssigneeData] =
          payload[key as keyof CaseAssigneeData];
      }
    });
  },
  SET_REASON_CODE(state: any, entry: ICaseReasonCode[]) {
    state.reasonCodes = entry;
  },
  SET_SELECTED_USER(state: any, entry: IDataAPIUser[]) {
    state.currentAssignees = entry;
  },
  REGISTER_CHANGE(state: any, hasNotChanged: boolean) {
    state.changesExist = !hasNotChanged;
  },
  SHOW_CHANGE_DIALOG(state: any, show: boolean) {
    state.showChangeDialog = show;
  },
  SET_CURRENT_CASE_STATUS(
    state: any,
    payload: {
      original: IDataAPICaseStatus | null;
      currentValue: CaseStatusValue | null;
    }
  ) {
    state.original = payload.original;
    state.currentValue = payload.currentValue;
  }
};

const actions = {
  async FETCH_ASSIGNED_BY_ALL({ commit }: any, id: string) {
    const endpointRaw = createServerEndpoint("/api/cases/{case_id}/visibility");
    const endpoint = endpointRaw.replace("{case_id}", id);

    try {
      const { data } = await axios.get<CaseAssignmentResponse[]>(endpoint);
      commit("UPDATE_ASSIGNEE_DATA", { list: data });
    } catch (err) {
      console.error(err);
      commit("UPDATE_ASSIGNEE_DATA", { list: [] });
    }
  },

  async FETCH_ASSIGNABLE_BY_ME({ commit }: any) {
    const endpoint = createServerEndpoint("/users/assignment-options");

    try {
      const { data } = await axios.get<IDataAPIUser[]>(endpoint);
      commit("UPDATE_ASSIGNEE_DATA", { options: data });
    } catch (err) {
      console.error(err);
      commit("UPDATE_ASSIGNEE_DATA", { options: [] });
    }
  },

  async FETCH_ASSIGNED_BY_ME({ commit }: any, id: string) {
    const endpointRaw = createServerEndpoint("/api/cases/{case_id}/assignment");
    const endpoint = endpointRaw.replace("{case_id}", id);

    try {
      const { data } = await axios.get<CaseAssignmentResponse[]>(endpoint);
      commit("UPDATE_ASSIGNEE_DATA", { choice: data });
    } catch (err) {
      console.error(err);
      commit("UPDATE_ASSIGNEE_DATA", { choice: [] });
    }
  },

  async UPDATE_CASE_ASSIGNMENT(
    { dispatch }: any,
    input: UpdateCaseAssignmentRequest
  ) {
    const { id, selectedUsers, difference } = input;
    const endpointRaw = createServerEndpoint("/api/cases/{case_id}/assignment");
    const endpoint = endpointRaw.replace("{case_id}", id);

    const payload: ICaseAssignmentBodyReq[] = [...selectedUsers, ...difference];

    try {
      await axios.post<CaseAssignmentResponse[]>(endpoint, payload);

      // Optionally refresh the current value after the update
      await dispatch("FETCH_ASSIGNED_BY_ALL", id);

      // Reload the page to get accurate data
      location.reload();
    } catch (err) {
      console.error(err);
    }
  },

  async FETCH_CURRENT_CASE_STATUS({ commit }: any, id: string) {
    const endpointRaw = createServerEndpoint("/api/cases/{case_id}/status");
    const endpoint = endpointRaw.replace("{case_id}", id);

    try {
      const { data } = await axios.get<IDataAPICaseStatus>(endpoint);
      commit("SET_CURRENT_CASE_STATUS", {
        original: data,
        currentValue: data ? data.status : data
      });
    } catch (err) {
      console.error(err);
      commit("SET_CURRENT_CASE_STATUS", {
        original: null,
        currentValue: null
      });
    }
  },

  async UPDATE_CURRENT_CASE_STATUS(_: any, input: UpdateCaseStatusRequest) {
    const { id, ...payload } = input;
    const endpointRaw = createServerEndpoint("/api/cases/{case_id}/status");
    const endpoint = endpointRaw.replace("{case_id}", id);

    try {
      await axios.put<IDataAPICaseStatus>(endpoint, payload);

      // Reload the page to get accurate data
      if (payload.status !== "Re-opened") {
        location.reload();
      }
    } catch (err) {
      console.error(err);
    }
  },

  async SET_SELECTED_USER({ commit }: any, entry: IDataAPIUser[]) {
    commit("SET_SELECTED_USER", entry);
  }
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions
};
